import React, { useMemo, useCallback } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { QueryLoader } from "@jsq/ds/core";
import { AccountTypeForm } from "../../components/AccountTypeForm";
import { DocumentCollectorInvestor } from "../../components/documents/DocumentCollectorInvestor";
import { DocumentDetailsInvestor } from "../../components/documents/DocumentDetailsInvestor";
import { HybridCategorizationPage } from "../../components/HybridCategorizationPage";
import { JointLandingScreen } from "../../components/JointLandingScreen";
import { useGetComplianceCaseQuery } from "../../graphql/supergraphHooks";
import { isJoinAccountType, resolvedAccountType } from "../../utils/account_types";
import { clientContext } from "../../utils/graphql_utils";
import { readBooleanProperty } from "../../utils/json_utils";
import { ComplianceCaseContext, ComplianceContextValue } from "../ComplianceCaseContext";
import { ComplianceRoutes } from "../shared/ComplianceRoutes";
import { InitialPage } from "./types";
import { getInitialPage } from "./utils";

export const StandAloneCompliance = ({
  componentContext,
  onStepCompletion,
}: {
  componentContext: ComplianceContextValue;
  onStepCompletion: () => void;
}) => {
  const location = useLocation();
  const { basePath, isReadOnly, skipDetailsForRootProfile, enableLPUXv2 } = useMemo(
    () => ({
      basePath: componentContext.basePath,
      isReadOnly: componentContext.isReadOnly,
      skipDetailsForRootProfile: componentContext.skipDetailsForRootProfile,
      enableLPUXv2: componentContext.enableLPUXv2,
    }),
    [
      componentContext.basePath,
      componentContext.isReadOnly,
      componentContext.skipDetailsForRootProfile,
      componentContext.enableLPUXv2,
    ],
  );
  const caseResult = useGetComplianceCaseQuery({
    context: clientContext(),
    fetchPolicy: "network-only",
    variables: {
      caseId: componentContext.caseId,
    },
  });

  const checkAccessAndRender = useCallback(
    (Component: React.ComponentType) => {
      return isReadOnly ? <Redirect to={basePath} /> : <Component />;
    },
    [isReadOnly, basePath],
  );

  const baseRouteHandler = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const showAccountForm = searchParams.get("show_account_type") === "true";
    if (showAccountForm) {
      return <AccountTypeForm />;
    }
    const complianceCase = caseResult.data?.complianceCase;
    const investingProfile = complianceCase?.investingProfile;
    const accountType = resolvedAccountType(investingProfile);
    const iraConsentStatus = readBooleanProperty(
      investingProfile?.metaData,
      "is_ira_account",
      null,
    );
    const detailsCompleted = isJoinAccountType(accountType)
      ? !!investingProfile?.legalName
      : !!investingProfile?.amlEntityTypeId;
    const documentsCompleted =
      !!investingProfile?.caseDataEntryStatus?.documentsCompleted ||
      isJoinAccountType(accountType);
    const areOwnersCompleted = !!investingProfile?.caseDataEntryStatus?.ownersCompleted;
    const areControllersCompleted =
      !!investingProfile?.caseDataEntryStatus?.controllersCompleted;

    const initialPage = getInitialPage({
      accountType,
      iraConsentStatus,
      detailsCompleted,
      documentsCompleted,
      areOwnersCompleted,
      areControllersCompleted,
    });

    switch (initialPage) {
      case InitialPage.NoRootProfile:
        return <AccountTypeForm />;
      case InitialPage.Individual:
        if (skipDetailsForRootProfile) {
          return enableLPUXv2 ? (
            <DocumentDetailsInvestor onStepCompletion={onStepCompletion} />
          ) : (
            <DocumentCollectorInvestor onStepCompletion={onStepCompletion} />
          );
        } else {
          return <Redirect to={`${basePath}/overview/${investingProfile?.id}`} />;
        }
      case InitialPage.Joint:
        if (skipDetailsForRootProfile) {
          return <JointLandingScreen onStepCompletion={onStepCompletion} />;
        } else {
          return <Redirect to={`${basePath}/overview/${investingProfile?.id}`} />;
        }
      case InitialPage.IraCategorization:
        return <Redirect to={`${basePath}/ira-categorization`} />;
      case InitialPage.Overview:
        return <Redirect to={`${basePath}/overview/${investingProfile?.id}`} />;
      case InitialPage.Tree:
        return <Redirect to={`${basePath}/overview`} />;
    }
  }, [
    caseResult.data?.complianceCase,
    skipDetailsForRootProfile,
    basePath,
    onStepCompletion,
    enableLPUXv2,
    location.search,
  ]);

  return (
    <ComplianceCaseContext.Provider value={componentContext}>
      <Switch>
        <Route
          path={basePath}
          exact
          render={() => <QueryLoader result={caseResult}>{baseRouteHandler}</QueryLoader>}
        />
        <Route path={`${basePath}/ira-categorization`} exact>
          {checkAccessAndRender(HybridCategorizationPage)}
        </Route>
        <ComplianceRoutes
          basePath={basePath}
          onStepCompletion={onStepCompletion}
          routeGuard={checkAccessAndRender}
        />
      </Switch>
    </ComplianceCaseContext.Provider>
  );
};
